import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

const fmtError = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
};

export const SignupModal = ({ open, onOpenChange, onSuccess }) => {
  const { signup, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({
    full_name: "", email: "", password: "",
    city: "", country: "New Zealand", phone: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      if (mode === "signup") {
        await signup(form);
      } else {
        await login({ email: form.email, password: form.password });
      }
      onOpenChange(false);
      if (onSuccess) onSuccess();
      else navigate("/onboarding");
    } catch (e) {
      setErr(fmtError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const isSignup = mode === "signup";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="signup-modal"
        className="bg-[#FDFBF7] border-[#E5E1D8] rounded-3xl w-[calc(100%-1.5rem)] max-w-md p-6 md:p-8 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <span className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">
            {isSignup ? "Start her registry" : "Welcome back"}
          </span>
          <DialogTitle className="font-display text-3xl text-[#2D3732] mt-3 leading-tight">
            {isSignup ? <>Let's build<br /><span className="italic text-[#D98A71]">something for her.</span></> : <>Sign back<br /><span className="italic text-[#D98A71]">in.</span></>}
          </DialogTitle>
          <DialogDescription className="text-[#5C6B62] mt-3">
            {isSignup
              ? "Just the essentials — you can edit anything later."
              : "Continue with the email you signed up with."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-4">
          {isSignup && (
            <Field label="Full name" testId="signup-name">
              <input
                data-testid="signup-name-input"
                type="text" value={form.full_name} onChange={set("full_name")}
                placeholder="e.g. Amelia Wai"
                className={inputCls}
              />
            </Field>
          )}
          <Field label="Email">
            <input
              data-testid="signup-email-input"
              type="email" value={form.email} onChange={set("email")}
              placeholder="you@example.com"
              className={inputCls}
            />
          </Field>
          <Field label="Password">
            <input
              data-testid="signup-password-input"
              type="password" value={form.password} onChange={set("password")}
              placeholder="At least 6 characters"
              className={inputCls}
            />
          </Field>
          {isSignup && (
            <Field label="City or region">
              <input
                data-testid="signup-city-input"
                type="text" value={form.city} onChange={set("city")}
                placeholder="e.g. Ōtautahi Christchurch"
                className={inputCls}
              />
              <p className="mt-2 text-[11px] text-[#5C6B62]">
                Guests will see your city on your registry so they know where care is heading.
              </p>
            </Field>
          )}
          {isSignup && (
            <Field label="Country">
              <input
                data-testid="signup-country-input"
                type="text" value={form.country} onChange={set("country")}
                placeholder="New Zealand"
                className={inputCls}
              />
            </Field>
          )}
          {isSignup && (
            <Field label="Mobile / WhatsApp (optional)">
              <input
                data-testid="signup-phone-input"
                type="tel" value={form.phone} onChange={set("phone")}
                placeholder="e.g. +64 21 555 0100"
                className={inputCls}
              />
              <p className="mt-2 text-[11px] text-[#5C6B62]">
                Only used by the Held concierge team to coordinate deliveries with you. Never shared with guests.
              </p>
            </Field>
          )}
        </div>

        {err && <p data-testid="signup-error" className="mt-4 text-sm text-[#D98A71]">{err}</p>}

        <button
          data-testid={isSignup ? "signup-submit" : "login-submit"}
          onClick={submit}
          disabled={busy}
          className="mt-6 w-full rounded-full bg-[#D98A71] hover:bg-[#c67a63] text-[#FDFBF7] py-4 text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          {isSignup ? "Create my registry" : "Sign in"}
        </button>

        <p className="mt-5 text-center text-xs text-[#5C6B62]">
          {isSignup ? "Already have an account?" : "New to Held?"}{" "}
          <button
            data-testid="toggle-auth-mode"
            onClick={() => { setMode(isSignup ? "login" : "signup"); setErr(""); }}
            className="underline underline-offset-4 text-[#2D3732] hover:text-[#D98A71] transition-colors"
          >
            {isSignup ? "Sign in" : "Create an account"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};

const inputCls =
  "w-full h-12 rounded-2xl border border-[#E5E1D8] bg-white/60 px-4 focus:outline-none focus:border-[#D98A71] transition-colors placeholder:text-[#5C6B62]/50";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);
